import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';

export const formatText = (text: string) => {
  // Replace newline characters with <br/> elements
  const replaceNewline = text.split('\n').map((part, index) => (
    <span key={index}>
      {part}
      <br />
    </span>
  ));

  return replaceNewline;
};

export const SafeFormattedText = ({ text }: { text?: string }) => {
  if (!text) {
    return null;
  }
  const sanitizedMarkdown = DOMPurify.sanitize(text);
  return (
    <ReactMarkdown className="markdownContainer">
      {sanitizedMarkdown}
    </ReactMarkdown>
  );
};

export const formatNumber = (num: string | number | undefined) => {
  if (!num) {
    return '0';
  }
  return Number(num).toLocaleString('de-DE');
};

export const capitalizeFirstLetter = (string: string | undefined | null) => {
  if (!string) {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const lowercaseFirstLetter = (string: string | undefined | null) => {
  if (!string) {
    return '';
  }
  return string.charAt(0).toLowerCase() + string.slice(1);
};

export const variableToString = (variable: any): string => {
  if (typeof variable === 'string') {
    return variable;
  }

  if (typeof variable === 'number' || typeof variable === 'boolean') {
    return variable.toString();
  }

  if (variable === null || variable === undefined) {
    return String(variable); // Converts null to 'null', and undefined to 'undefined'
  }

  if (Array.isArray(variable)) {
    return JSON.stringify(variable);
  }

  if (typeof variable === 'object') {
    return JSON.stringify(variable, null, 2); // Beautified JSON string
  }

  return 'Unknown Type';
};

interface StyleWordsParams {
  text: string;
  wordsToStyle: string[];
  style?: React.CSSProperties;
  limitTimesToStyleProvidedWords?: number;
}

export const styleWords = ({
  text,
  wordsToStyle,
  style = {},
  limitTimesToStyleProvidedWords = Infinity,
}: StyleWordsParams) => {
  const words = text.split(' ');
  const styledWordsCount: Record<string, number> = {};

  return (
    <>
      {words.map((word, index) => {
        const cleanWord = word.replace(/[.,!?;:]$/, '');
        const shouldStyleWord =
          wordsToStyle.includes(cleanWord) &&
          (styledWordsCount[cleanWord] || 0) < limitTimesToStyleProvidedWords;

        if (shouldStyleWord) {
          styledWordsCount[cleanWord] = (styledWordsCount[cleanWord] || 0) + 1;
        }

        return (
          <span key={index} style={shouldStyleWord ? style : {}}>
            {word}{' '}
          </span>
        );
      })}
    </>
  );
};
